import React, { useState, useEffect } from "react";
import { ButtonConfig, Action } from "./InterfaceButtonConfig";
import { Button, Flex, Input, message, Select } from "antd";
import {
  CheckCircleOutlined,
  CheckOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  ExportOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  UpOutlined,
} from "@ant-design/icons";

// Ant Design Select Option type
const { Option } = Select;

const InterfaceActionBuilder: React.FC = () => {
  const generateId = () =>
    Date.now().toString() + Math.random().toString(36).substring(2, 6); // NOSONAR : ignore

  const [buttons, setButtons] = useState<ButtonConfig[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [jsonInput, setJsonInput] = useState("");

  const selected = buttons.find((btn) => btn.id === selectedId) || null;

  // Load JSON
  const loadJson = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      if (!Array.isArray(parsed)) throw new Error("Expected an array");
      const withIds = parsed.map((btn: any) => ({ id: generateId(), ...btn }));
      setButtons(withIds);
      setSelectedId(null);
    } catch {
      message.error("Invalid JSON format");
    }
  };

  // Update Query JSON
  const updateQueryJsonInput = () => {
    try {
      const parseddata = JSON.parse(jsonInput);
      const stringified = JSON.stringify(parseddata);
      const query = `Update Interface SET InterfaceActionConfig = '${stringified}' Where Id = ??`;
      navigator.clipboard
        .writeText(query)
        .then(() => {
          message.success("JSON copied to clipboard!");
        })
        .catch(() => {
          message.error("Failed to copy text!");
        });
    } catch {
      message.error("Invalid JSON format");
      return jsonInput; // Return original if parsing fails
    }
  };

  const updateSelected = (updated: ButtonConfig) => {
    setButtons((btns) =>
      btns.map((btn) => (btn.id === updated.id ? updated : btn))
    );
  };

  // Button Config
  const addButton = () => {
    const newBtn: ButtonConfig = {
      id: generateId(),
      type: "Button",
      mode: "",
      label: "",
      icon: "",
      className: "",
      actionType: "",
      visibility: { edit: [], formMode: [] },
      actions: [],
    };
    setButtons([...buttons, newBtn]);
    setSelectedId(newBtn.id);
  };

  // Delete Button
  const deleteButton = (id: string) => {
    setButtons((btns) => btns.filter((b) => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  // validate JSON
  const validateJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      message.success("JSON is correct format!");
      return JSON.stringify(parsed, null, 2); // Pretty print with 2 spaces
    } catch {
      message.error("Invalid JSON format");
      return json; // Return original if parsing fails
    }
  };

  // beautify JSON
  const beautifyJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      return JSON.stringify(parsed, null, 2); // Pretty print with 2 spaces
    } catch {
      message.error("Invalid JSON format for beautification");
      return json; // Return original if parsing fails
    }
  };

  // minify JSON
  const minifyJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      return JSON.stringify(parsed); // Minified JSON
    } catch {
      message.error("Invalid JSON format for minification");
      return json; // Return original if parsing fails
    }
  };

  // Visibility Action

  const handleVisibilityChange = (type: string, value: string) => {
    if (selected) {
      const updatedVisibility = {
        ...(selected.visibility ?? { edit: [], formMode: [] }), // Default if undefined
        [type]: value.split(",").map((item: string) => item.trim()),
      };
      updateSelected({ ...selected, visibility: updatedVisibility });
    }
  };

  useEffect(() => {
    if (buttons.length === 0) {
      setJsonInput(""); // or setJsonInput(null); if you prefer null
    } else {
      const cleaned = buttons.map(({ id, ...rest }) => rest);
      setJsonInput(JSON.stringify(cleaned, null, 2));
    }
  }, [buttons]);

  // Handle Action Mode Change
  const handleActionModeChange = (mode: string) => {
    if (selected) {
      updateSelected({ ...selected, mode });
    }
  };

  // Handle Exec Type Change
  const handleExecTypeChange = (execType: string, index: number) => {
    if (selected) {
      const updatedActions = [...selected.actions];
      updatedActions[index] = { ...updatedActions[index], execType };
      updateSelected({ ...selected, actions: updatedActions });
    }
  };

  // Move buttons up
  const moveButtonUp = (id: string) => {
    const index = buttons.findIndex((btns) => btns.id === id);
    if (index > 0) {
      const newButtons = [...buttons];
      const [movedButton] = newButtons.splice(index, 1);
      newButtons.splice(index - 1, 0, movedButton);
      setButtons(newButtons);
    }
  };

  // Move buttons down
  const moveButtonDown = (id: string) => {
    const index = buttons.findIndex((btns) => btns.id === id);
    if (index < buttons.length - 1) {
      const newButtons = [...buttons];
      const [movedButton] = newButtons.splice(index, 1);
      newButtons.splice(index + 1, 0, movedButton);
      setButtons(newButtons);
    }
  };

  // Move Action up
  const moveActionUp = (actionIndex: number) => {
    if (actionIndex > 0) {
      const updatedActions = [...selected!.actions];
      const [movedAction] = updatedActions.splice(actionIndex, 1);
      updatedActions.splice(actionIndex - 1, 0, movedAction);
      updateSelected({ ...selected!, actions: updatedActions });
    }
  };

  // Move Action down
  const moveActionDown = (actionIndex: number) => {
    if (actionIndex < selected!.actions.length - 1) {
      const updatedActions = [...selected!.actions];
      const [movedAction] = updatedActions.splice(actionIndex, 1);
      updatedActions.splice(actionIndex + 1, 0, movedAction);
      updateSelected({ ...selected!, actions: updatedActions });
    }
  };

  const handleParamNameChange = (
    actionIndex: number,
    paramIndex: number,
    newName: any
  ) => {
    const updatedActions = (selected?.actions ?? []).map((action, aIndex) => {
      if (aIndex !== actionIndex) return action;

      const updatedExecParams = action.execParams.map((param, pIndex) => {
        if (pIndex !== paramIndex) return param;
        return { ...param, name: newName };
      });

      return { ...action, execParams: updatedExecParams };
    });

    updateSelected({ ...selected, actions: updatedActions });
  };

  return (
    <div className="App">
      <h4>Interface action JSON builder</h4>

      <Input.TextArea
        rows={10}
        placeholder="Paste JSON here..."
        value={jsonInput}
        onChange={(e) => setJsonInput(e.target.value)}
        style={{ resize: "none", width: "100%" }} // Make it responsive
      />

      <div style={{ marginTop: 20, marginBottom: 8 }}>
        <Flex vertical gap="small">
          <Flex gap="small" wrap>
            <Button
              onClick={loadJson}
              color="primary"
              variant="solid"
              icon={<ReloadOutlined />}
            >
              Load JSON
            </Button>

            <Button
              onClick={() => {
                try {
                  const parseddata = JSON.parse(jsonInput);
                  console.log(parseddata);
                  navigator.clipboard.writeText(jsonInput);
                  message.success("JSON copied to clipboard!");
                } catch {
                  message.error("Invalid JSON format");
                  return jsonInput; // Return original if parsing fails
                }
              }}
              color="pink"
              variant="solid"
              icon={<CopyOutlined />}
            >
              Copy JSON
            </Button>

            <Button
              onClick={() => {
                const beautified = beautifyJson(jsonInput);
                setJsonInput(beautified);
                message.success("Beautified JSON Done!");
              }}
              color="purple"
              variant="solid"
              icon={<ExportOutlined />}
            >
              Beautify
            </Button>

            <Button
              onClick={() => {
                const minified = minifyJson(jsonInput);
                setJsonInput(minified);
                message.success("Minified JSON Done!");
              }}
              color="orange"
              variant="solid"
              icon={<MinusCircleOutlined />}
            >
              Minify{" "}
            </Button>

            <Button
              onClick={() => {
                const validateJsondata = validateJson(jsonInput);
                setJsonInput(validateJsondata);
              }}
              color="danger"
              variant="solid"
              icon={<CheckCircleOutlined />}
            >
              Validate JSON{" "}
            </Button>

            <Button
              onClick={updateQueryJsonInput}
              color="cyan"
              variant="solid"
              icon={<CheckOutlined />}
            >
              Copy JSON Query
            </Button>
          </Flex>
        </Flex>
      </div>

      <h3>Buttons</h3>

      {buttons.map((btn) => (
        <div
          key={btn.id}
          style={{ marginBottom: 8, padding: 6, border: "1px solid #ccc" }}
        >
          <Flex vertical gap="small">
            <Flex gap="small" wrap>
              <b>{btn.label || "Untitled"}</b> [{btn.mode}]
              <Button
                onClick={() => setSelectedId(btn.id)}
                color="primary"
                variant="dashed"
                size="small"
                icon={<EditOutlined />}
              ></Button>
              <Button
                onClick={() => deleteButton(btn.id)}
                color="danger"
                variant="dashed"
                size="small"
                icon={<DeleteOutlined />}
              ></Button>
              <Button
                onClick={() => moveButtonUp(btn.id)}
                color="geekblue"
                variant="dashed"
                size="small"
                icon={<UpOutlined />}
              />
              <Button
                onClick={() => moveButtonDown(btn.id)}
                color="magenta"
                variant="dashed"
                size="small"
                icon={<DownOutlined />}
              />
            </Flex>
          </Flex>
        </div>
      ))}

      <Button
        onClick={addButton}
        color="primary"
        variant="solid"
        icon={<PlusCircleOutlined />}
      >
        Add New Button
      </Button>

      {selected && (
        <div style={{ marginTop: 20 }}>
          <h3>Edit Button</h3>

          <table className="table table-bordered">
            <thead style={{ display: "none" }}>
              <tr>
                <th scope="col"></th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Label Name</td>
                <td>
                  <Input
                    placeholder="Button label to be visible on screen"
                    value={selected.label}
                    onChange={(e) =>
                      updateSelected({ ...selected, label: e.target.value })
                    }
                  />
                </td>
              </tr>
              <tr>
                <td>Icon Name</td>
                <td>
                  <Input
                    placeholder="Eg - EditOutlined/HistoryOutlined"
                    value={selected.icon}
                    onChange={(e) =>
                      updateSelected({ ...selected, icon: e.target.value })
                    }
                  />
                  <span>
                    <strong>Refer:</strong>{" "}
                    <a
                      href="https://ant.design/components/icon"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      List of icons
                    </a>
                  </span>
                </td>
              </tr>
              <tr>
                <td>Class Name</td>
                <td>
                  <Input
                    placeholder="Button class name eg:btn-submit"
                    value={selected.className}
                    onChange={(e) =>
                      updateSelected({ ...selected, className: e.target.value })
                    }
                  />
                </td>
              </tr>
              <tr>
                <td>Button Type</td>
                <td>
                  <Input
                    placeholder="Button Type"
                    value={selected.type}
                    onChange={(e) =>
                      updateSelected({ ...selected, type: e.target.value })
                    }
                    disabled
                  />
                </td>
              </tr>
              <tr>
                <td>Action Mode</td>
                <td>
                  <Select
                    value={selected.mode}
                    onChange={handleActionModeChange}
                    style={{ width: "100%" }}
                  >
                    <Select.Option value="">Select</Select.Option>
                    <Select.Option value="Create">Create</Select.Option>
                    <Select.Option value="Edit">Edit</Select.Option>
                    <Select.Option value="All">All</Select.Option>
                  </Select>
                  <span>
                    <strong>Note:</strong> Whether the button is visibile on
                    Create / Edit or All Mode
                  </span>
                </td>
              </tr>
              <tr>
                <td>Action Type</td>
                <td>
                  <Input
                    placeholder="System defined Action Type : DynamicFormActionSubmit/DynamicFormActionFetch/DynamicFormActionUpdate"
                    value={selected.actionType}
                    onChange={(e) =>
                      updateSelected({
                        ...selected,
                        actionType: e.target.value,
                      })
                    }
                  />

                  <span>
                    <strong>Note:</strong> System defined Action Type :
                    DynamicFormActionSubmit/DynamicFormActionFetch/DynamicFormActionUpdate
                  </span>
                </td>
              </tr>
              <tr>
                <td>Visibility</td>
                <td>
                  <Flex gap="small">
                    <div>
                      <Input
                        placeholder="Visibility (Edit)"
                        value={selected.visibility?.edit.join(", ")}
                        onChange={(e) =>
                          handleVisibilityChange("edit", e.target.value)
                        }
                        style={{ width: "525px" }}
                      />
                    </div>
                    <div>
                      <Input
                        placeholder="Visibility (Form Mode)"
                        value={selected.visibility?.formMode.join(", ")}
                        onChange={(e) =>
                          handleVisibilityChange("formMode", e.target.value)
                        }
                        style={{ width: "525px" }}
                      />
                    </div>
                  </Flex>
                </td>
              </tr>
            </tbody>
          </table>

          <h4>Actions</h4>

          {selected.actions.map((act, i) => (
            <div
              key={i} // NOSONAR : ignore
              style={{ border: "1px dashed gray", padding: 5, marginBottom: 5 }}
            >
              <Flex gap="small" wrap>
                <Button
                  onClick={() => moveActionUp(i)}
                  color="geekblue"
                  variant="dashed"
                  size="small"
                  icon={<UpOutlined />}
                />
                <Button
                  onClick={() => moveActionDown(i)}
                  color="magenta"
                  variant="dashed"
                  size="small"
                  icon={<DownOutlined />}
                />
              </Flex>

              <label htmlFor="ActionId">Action ID: </label>
              <Input
                id="ActionId"
                placeholder="Should be unique for each action"
                value={act.actionID}
                onChange={(e) =>
                  updateSelected({
                    ...selected,
                    actions: selected.actions.map((a, index) =>
                      index === i ? { ...a, actionID: e.target.value } : a
                    ),
                  })
                }
              />
              <label>
                Execution Type:{" "}
                <span>(Select whether to execute SP/Query or Function)</span>
              </label>
              <Select
                value={selected.actions[0]?.execType} // Assuming actions are non-empty
                onChange={(value) => handleExecTypeChange(value, 0)} // Change for first action (can be customized)
                style={{ width: "100%" }}
              >
                <Select.Option value="">Select</Select.Option>
                <Select.Option value="SP">SP</Select.Option>
                <Select.Option value="Query">Query</Select.Option>
                <Select.Option value="Function">Function</Select.Option>
              </Select>
              <label htmlFor="ExecData">Execution Data:</label>
              <Input
                id="ExecData"
                placeholder="Mention either function name, stored procedure name or query basis the execution type"
                value={act.execData}
                onChange={(e) =>
                  updateSelected({
                    ...selected,
                    actions: selected.actions.map((a, index) =>
                      index === i ? { ...a, execData: e.target.value } : a
                    ),
                  })
                }
              />

              <div style={{ marginTop: 5 }}>
                <label htmlFor="ExecParams">Execution Parameters:</label>
                {act.execParams.map((p, j) => (
                  <Input
                    id="ExecParams"
                    key={j} // NOSONAR : ignore
                    placeholder="Parameters to be sent to function. Parameter name should match field backend name"
                    value={p.name}
                    onChange={(e) =>
                      handleParamNameChange(i, j, e.target.value)
                    }
                  />
                ))}
                <Button
                  onClick={() => {
                    updateSelected({
                      ...selected,
                      actions: selected.actions.map((a, index) =>
                        index === i
                          ? {
                              ...a,
                              execParams: [...a.execParams, { name: "" }],
                            }
                          : a
                      ),
                    });
                  }}
                  color="purple"
                  variant="dashed"
                  size="small"
                  icon={<PlusOutlined />}
                >
                  Param
                </Button>
              </div>
            </div>
          ))}

          <Button
            onClick={() => {
              const newAction: Action = {
                actionID: "",
                execType: "",
                execData: "",
                execParams: [],
              };
              updateSelected({
                ...selected,
                actions: [...selected.actions, newAction],
              });
            }}
            color="cyan"
            variant="solid"
            icon={<PlusCircleOutlined />}
          >
            Add New Action
          </Button>
        </div>
      )}
    </div>
  );
};

export default InterfaceActionBuilder;
